/**
 * Profile settings.
 *
 * Each field saves on its own rather than behind one "Save everything" button:
 * the handle can fail uniqueness, the avatar can fail ownership, and a single
 * submit would have to either roll all of it back or half-apply and explain
 * which half. Separate saves make each failure local to the thing that failed.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { AVATAR_PRESETS, Avatar, type Identity } from '../components/Avatar'
import { AssetLogo } from '../components/AssetLogo'
import { EmptyState, Skeleton } from '../components/ui'
import { useAvatarOptions, useUpdateProfile, useUsernameCheck } from '../hooks/queries'
import { useAuth } from '../hooks/useAuth'
import { ApiError, type AvatarKind, type User } from '../lib/api'

export function SettingsPage() {
  const { user, connectAndSignIn } = useAuth()

  if (!user) {
    return (
      <div className="py-10">
        <EmptyState
          title="Sign in to edit your profile"
          body="Your profile belongs to your wallet, so it needs a signed-in session to change."
          action={
            <button className="btn btn-primary btn-sm" onClick={() => void connectAndSignIn()}>
              Connect wallet
            </button>
          }
        />
      </div>
    )
  }

  return (
    <div className="py-6">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Profile settings</h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
            How you appear on the leaderboard, the live ticker and your public profile.
          </p>
        </div>
        <Link to="/me" className="btn btn-ghost btn-sm">
          Back to dashboard
        </Link>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        <div className="space-y-5">
          <UsernameField user={user} />
          <BioField user={user} />
          <PrivacyField user={user} />
        </div>

        <AvatarField user={user} />
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Shared furniture
 * ------------------------------------------------------------------ */

function Field({
  title,
  hint,
  children,
  footer,
}: {
  title: string
  hint?: string
  children: React.ReactNode
  footer?: React.ReactNode
}) {
  return (
    <section className="panel p-5">
      <h2 className="text-base font-semibold">{title}</h2>
      {hint && (
        <p className="mt-1 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
          {hint}
        </p>
      )}
      <div className="mt-4">{children}</div>
      {footer}
    </section>
  )
}

/** One line of feedback under a field, coloured by outcome. */
function Note({ tone, children }: { tone: 'ok' | 'bad' | 'muted'; children: React.ReactNode }) {
  const color =
    tone === 'ok' ? 'var(--color-up)' : tone === 'bad' ? 'var(--color-down)' : 'var(--color-faint)'
  return (
    <p className="mt-2 text-xs" style={{ color }} role={tone === 'bad' ? 'alert' : undefined}>
      {children}
    </p>
  )
}

const inputClass = 'panel-2 w-full px-3 py-2 text-sm outline-none'

/* ------------------------------------------------------------------ *
 * Username
 * ------------------------------------------------------------------ */

function UsernameField({ user }: { user: User }) {
  const [value, setValue] = useState(user.username ?? '')
  const [saved, setSaved] = useState(false)
  const update = useUpdateProfile()

  // Debounced so the availability endpoint sees one request per pause, not one
  // per keystroke — and so the "taken" warning does not flicker while typing.
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value.trim().toLowerCase()), 350)
    return () => window.clearTimeout(id)
  }, [value])

  const unchanged = debounced === (user.username ?? '')
  const check = useUsernameCheck(unchanged ? '' : debounced)

  const status = useMemo(() => {
    if (unchanged) return null
    if (debounced.length < 3) return { tone: 'muted' as const, text: 'Keep going…' }
    if (check.isLoading) return { tone: 'muted' as const, text: 'Checking…' }
    if (!check.data) return null
    if (check.data.available) return { tone: 'ok' as const, text: `${debounced} is available.` }
    const reason =
      check.data.reason === 'taken'
        ? 'That name is taken.'
        : check.data.reason === 'reserved'
          ? 'That name is reserved.'
          : check.data.help
    return { tone: 'bad' as const, text: reason }
  }, [check.data, check.isLoading, debounced, unchanged])

  const canSave = !unchanged && check.data?.available === true && !update.isPending

  return (
    <Field
      title="Username"
      hint="3–20 characters, starting with a letter. Lowercase letters, numbers and underscores. This is your profile URL."
    >
      <div className="flex flex-wrap gap-2">
        <div className="flex min-w-[220px] flex-1 items-center gap-0">
          <span
            className="panel-2 border-r-0 px-3 py-2 text-sm"
            style={{ color: 'var(--color-faint)' }}
          >
            exlyr.io/u/
          </span>
          <input
            className={inputClass}
            value={value}
            onChange={(e) => {
              setValue(e.target.value.toLowerCase())
              setSaved(false)
            }}
            spellCheck={false}
            autoComplete="off"
            maxLength={20}
            aria-label="Username"
          />
        </div>
        <button
          className="btn btn-primary btn-sm"
          disabled={!canSave}
          onClick={() => {
            update.mutate(
              { username: debounced },
              { onSuccess: () => setSaved(true) },
            )
          }}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>

      {update.error ? (
        <Note tone="bad">
          {update.error instanceof ApiError ? update.error.message : 'Could not save that name.'}
        </Note>
      ) : saved ? (
        <Note tone="ok">Saved.</Note>
      ) : status ? (
        <Note tone={status.tone}>{status.text}</Note>
      ) : null}
    </Field>
  )
}

/* ------------------------------------------------------------------ *
 * Bio
 * ------------------------------------------------------------------ */

function BioField({ user }: { user: User }) {
  const [value, setValue] = useState(user.bio ?? '')
  const [saved, setSaved] = useState(false)
  const update = useUpdateProfile()

  const dirty = value.trim() !== (user.bio ?? '')

  return (
    <Field title="Bio" hint="Shown on your public profile. 160 characters.">
      <textarea
        className={`${inputClass} min-h-[84px] resize-y`}
        value={value}
        maxLength={160}
        onChange={(e) => {
          setValue(e.target.value)
          setSaved(false)
        }}
        placeholder="Chasing the mythic band."
        aria-label="Bio"
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="tnum text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
          {value.length}/160
        </span>
        <button
          className="btn btn-primary btn-sm"
          disabled={!dirty || update.isPending}
          onClick={() =>
            update.mutate({ bio: value.trim() || null }, { onSuccess: () => setSaved(true) })
          }
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      {saved && !dirty && <Note tone="ok">Saved.</Note>}
    </Field>
  )
}

/* ------------------------------------------------------------------ *
 * Privacy
 * ------------------------------------------------------------------ */

function PrivacyField({ user }: { user: User }) {
  const update = useUpdateProfile()
  const isPublic = user.profilePublic

  return (
    <Field
      title="Public profile"
      hint="With this off, your results still count toward the site-wide numbers but appear on the leaderboard and the ticker as Anonymous, with no handle, no avatar and no address."
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm">
          <span className="font-semibold">{isPublic ? 'Public' : 'Private'}</span>
          <span style={{ color: 'var(--color-muted)' }}>
            {isPublic ? ': anyone can see your profile.' : ': your profile page is hidden.'}
          </span>
        </div>
        <button
          className="btn btn-ghost btn-sm"
          disabled={update.isPending}
          onClick={() => update.mutate({ profilePublic: !isPublic })}
          aria-pressed={isPublic}
        >
          {update.isPending ? 'Saving…' : isPublic ? 'Make private' : 'Make public'}
        </button>
      </div>
    </Field>
  )
}

/* ------------------------------------------------------------------ *
 * Avatar
 * ------------------------------------------------------------------ */

function AvatarField({ user }: { user: User }) {
  const { data, isLoading } = useAvatarOptions()
  const update = useUpdateProfile()

  const identity = (kind: AvatarKind, ref: string | null): Identity => ({
    username: user.username,
    handle: user.username ?? 'you',
    avatarKind: kind,
    avatarRef: ref,
    seed: user.avatarSeed,
  })

  const choose = (kind: AvatarKind, ref: string | null) =>
    update.mutate({ avatarKind: kind, avatarRef: ref })

  const selected = (kind: AvatarKind, ref: string | null) =>
    user.avatarKind === kind && (user.avatarRef ?? null) === ref

  const tile = (kind: AvatarKind, ref: string | null, key: string, label: string) => {
    const on = selected(kind, ref)
    return (
      <button
        key={key}
        onClick={() => choose(kind, ref)}
        disabled={update.isPending}
        aria-pressed={on}
        aria-label={label}
        title={label}
        className="grid place-items-center p-1.5 transition-colors"
        style={{
          border: `1px solid ${on ? 'var(--color-brand)' : 'var(--color-line)'}`,
          background: on ? 'var(--color-brand-wash)' : 'var(--color-surface-2)',
        }}
      >
        <Avatar identity={identity(kind, ref)} size={40} />
      </button>
    )
  }

  return (
    <div className="panel p-5">
      <h2 className="text-base font-semibold">Avatar</h2>
      <p className="mt-1 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
        Nothing is uploaded. Pick the mark in a tier colour, a token you have pulled, or the pattern
        generated from your wallet address.
      </p>

      <div className="mt-4 flex items-center gap-4 border-b pb-4">
        <Avatar identity={identity(user.avatarKind, user.avatarRef)} size={64} />
        <div className="text-xs" style={{ color: 'var(--color-faint)' }}>
          Current avatar
          {update.error && (
            <div style={{ color: 'var(--color-down)' }} role="alert">
              {update.error instanceof ApiError ? update.error.message : 'Could not change that.'}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        <div className="eyebrow mb-2">Generated</div>
        {tile('identicon', null, 'identicon', 'Generated from your wallet address')}
      </div>

      <div className="mt-4">
        <div className="eyebrow mb-2">Tier marks</div>
        <div className="flex flex-wrap gap-2">
          {AVATAR_PRESETS.map((preset) =>
            tile('preset', preset, preset, preset.replace('flask-', 'Elyxr mark, ')),
          )}
        </div>
      </div>

      <div className="mt-5">
        <div className="eyebrow mb-2">Tokens you have pulled</div>
        {isLoading ? (
          <Skeleton className="h-16" />
        ) : !data || data.tokens.length === 0 ? (
          <p className="text-xs" style={{ color: 'var(--color-faint)' }}>
            Open a pack and every token you pull unlocks here.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {data.tokens.slice(0, 40).map((token) => {
              // Kind-prefixed so the avatar renderer knows which logo
              // directory to read from — 'coin' and 'crypto' live in
              // different ones. See `logoPath` in AssetLogo.tsx.
              const ref = `${token.kind}:${token.symbol}`
              const on = selected('token', ref)
              return (
                <button
                  key={ref}
                  onClick={() => choose('token', ref)}
                  disabled={update.isPending}
                  aria-pressed={on}
                  aria-label={`${token.symbol}, pulled ${token.count} time${token.count === 1 ? '' : 's'}`}
                  title={`${token.symbol} · pulled ${token.count}×`}
                  className="grid place-items-center p-1.5 transition-colors"
                  style={{
                    border: `1px solid ${on ? 'var(--color-brand)' : 'var(--color-line)'}`,
                    background: on ? 'var(--color-brand-wash)' : 'var(--color-surface-2)',
                  }}
                >
                  <AssetLogo kind="coin" symbol={token.symbol} size={40} />
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
